import { SITE_LOCALE } from '@/i18n/locale';

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Extend the Window interface to include gtag
declare global {
  interface Window {
    gtag: (
      command: string,
      action: string,
      params?: {
        page_path?: string;
        page_title?: string;
        event_category?: string;
        event_label?: string;
        value?: number;
        [key: string]: unknown;
      }
    ) => void;
    dataLayer: Record<string, unknown>[];
    // Microsoft Clarity (index.html; production host only, so it may be undefined).
    clarity?: (command: string, ...args: unknown[]) => void;
    // Loads Clarity once consent is in place and a person has shown up (index.html; undefined off the production host).
    gdLoadClarity?: () => void;
    // The consent default for the visitor's region before any banner choice (index.html): true outside opt-in regions.
    gdConsentDefault?: boolean;
  }
}

export const usePageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Debounce tracking to avoid excessive calls
    const timeoutId = setTimeout(() => {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('config', 'G-6G2R4TZE4X', {
          page_path: location.pathname + location.search,
          language: SITE_LOCALE,
          locale: SITE_LOCALE,
          page_title: document.title,
          // Performance tracking
          custom_map: {
            'metric1': 'page_load_time'
          }
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location]);
};

export const trackEvent = (
  eventName: string, 
  category?: string, 
  label?: string, 
  value?: number
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, {
      event_category: category,
      event_label: label,
      value: value,
      // Add timing for performance events
      language: SITE_LOCALE,
      locale: SITE_LOCALE,
      send_to: 'G-6G2R4TZE4X'
    });
  }
};

// Enhanced tracking functions
export const trackPerformance = (metric: string, value: number) => {
  trackEvent('performance_metric', 'Performance', metric, value);
};

export const trackUserInteraction = (action: string, element: string) => {
  trackEvent('user_interaction', 'Engagement', `${action}_${element}`);
};

export const trackConversion = (type: string, value?: number) => {
  trackEvent('conversion', 'Business', type, value);
};

// Additional tracking functions for user interactions
export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent('button_click', 'User Interaction', `${buttonName}_${location}`);
};

export const trackFormSubmission = (formName: string, success: boolean) => {
  trackEvent('form_submission', 'Form', `${formName}_${success ? 'success' : 'error'}`);
};

export const trackVideoPlay = (videoTitle: string) => {
  trackEvent('video_play', 'Content', videoTitle);
};

export const trackDownload = (fileName: string) => {
  trackEvent('download', 'Content', fileName);
};

export const trackOutboundLink = (url: string, linkText: string) => {
  trackEvent('outbound_link', 'Navigation', `${linkText}_${url}`);
};

export const trackScroll = (depth: number) => {
  trackEvent('scroll_depth', 'Engagement', `${depth}%`, depth);
};

// --- CTA click tracking -----------------------------------------------------
// A single GA4 event ('cta_click') with parameters lets us differentiate every
// call-to-action in GA4 (filter/segment/Explore by cta_id; register cta_id and
// cta_location as custom dimensions). CTAs opt in via data attributes:
//   data-ga-cta="cut_my_bill"       (required — the stable action id)
//   data-ga-location="home_hero"    (optional — surface/section)
//   data-ga-text / data-ga-destination (optional overrides; otherwise derived)
// A single delegated listener (useCtaTracking) handles all of them, so it works
// for <Button>, <Link> and <a> alike and survives navigation (gtag beacons).

export interface CtaParams {
  cta_id: string;
  cta_location?: string;
  cta_text?: string;
  cta_destination?: string;
}

export const trackCta = ({ cta_id, cta_location, cta_text, cta_destination }: CtaParams) => {
  if (typeof window === 'undefined' || typeof window.gtag === 'undefined') return;
  window.gtag('event', 'cta_click', {
    cta_id,
    cta_location: cta_location || 'unknown',
    ...(cta_text ? { cta_text } : {}),
    ...(cta_destination ? { cta_destination } : {}),
    send_to: 'G-6G2R4TZE4X',
  });
};

// Delegated click listener: fires trackCta for any clicked element (or ancestor)
// carrying a data-ga-cta attribute. Mounted once at the app root.
export const useCtaTracking = () => {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const el = target?.closest<HTMLElement>('[data-ga-cta]');
      if (!el) return;
      const cta_id = el.dataset.gaCta;
      if (!cta_id) return;
      const text = (el.dataset.gaText || el.textContent || '').replace(/\s+/g, ' ').trim();
      const destination = el.dataset.gaDestination || el.getAttribute('href') || undefined;
      trackCta({
        cta_id,
        cta_location: el.dataset.gaLocation,
        cta_text: text ? text.slice(0, 100) : undefined,
        cta_destination: destination || undefined,
      });
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, []);
};

export default { usePageTracking, trackEvent, trackCta, useCtaTracking };
